import { Box, Paragraph } from '@zillow/constellation-10';
import { memo, useContext, useEffect, useRef, useState } from 'react';
import { shouldScrollToRenterProfileStore } from '../../../../lib/inboxStores';
import { InboxPageContext } from '../../../../contexts/InboxPageContextProvider';
import { InboxPageMobileView } from '../../../../types/InboxPageMobileView';
import useDevices from '../../../../hooks/useDevices';
import { useConversationMessagesQuery } from '../../../../hooks/useConversationMessagesQuery';
import type { ConversationMessagesData } from '../../../../utils/conversationMessageUtils';
import { getFormattedAttributesList } from '../../../../utils/renterProfileUtils';
import InboxSidebarSection from '../InboxSidebarSection';

type RenterProfileAttributeProps = {
  title: string;
  content: string;
};

export const selectRenterProfile = (data: ConversationMessagesData): ConversationMessagesData['renterProfile'] =>
  data.renterProfile;

const RenterProfileAttributes = (): JSX.Element | null => {
  const { conversationId, clientTimeZone, mobileView } = useContext(InboxPageContext);
  const { isDesktopView } = useDevices();
  const renterProfileAttributesRef = useRef<HTMLDivElement>(null);
  const shouldScrollToRenterProfile = shouldScrollToRenterProfileStore.useValue();
  // Cache-only read — `MessageListItems` owns the `getMessages` fetch + polling.
  const { data: renterProfile } = useConversationMessagesQuery({
    conversationId,
    clientTimeZone,
    enabled: false,
    select: selectRenterProfile,
  });
  const [isBackgroundHighlighted, setIsBackgroundHighlighted] = useState(false);

  // On mobile the sidebar is display:none until the RenterProfile view is active. The store is set
  // right after `changeMobileView`, whose route update lands a tick later, so the section isn't laid
  // out on the store's first tick and `scrollIntoView` would no-op. Gate the scroll on the section
  // being on screen (desktop sidebar, or the RenterProfile mobile view); the dep re-runs the effect
  // once the view flips so the scroll fires against a laid-out element.
  const isRenterProfileSectionVisible = isDesktopView || mobileView === InboxPageMobileView.RenterProfile;

  useEffect(() => {
    if (shouldScrollToRenterProfile && isRenterProfileSectionVisible && renterProfileAttributesRef.current) {
      renterProfileAttributesRef.current.scrollIntoView({
        behavior: 'smooth',
      });
      setIsBackgroundHighlighted(true);

      setTimeout(() => {
        setIsBackgroundHighlighted(false);
      }, 3000);

      shouldScrollToRenterProfileStore.set(false);
    }
  }, [shouldScrollToRenterProfile, isRenterProfileSectionVisible]);

  if (!renterProfile || renterProfile.isRenterProfileHidden) {
    return null;
  }

  const attributes = getFormattedAttributesList(renterProfile.attributes);
  if (attributes.length === 0) return null;

  return (
    <InboxSidebarSection
      title="Renter Profile"
      ref={renterProfileAttributesRef}
      isBackgroundHighlighted={isBackgroundHighlighted}
    >
      <>
        {attributes.map((attribute) => (
          <RenterProfileAttribute key={attribute.title} title={attribute.title} content={attribute.value} />
        ))}
      </>
    </InboxSidebarSection>
  );
};

const RenterProfileAttribute = memo(({ title = '', content = '' }: RenterProfileAttributeProps) => {
  return (
    <Box>
      <Paragraph textStyle="body-sm-bold" css={{ color: 'text.subtle', marginTop: 'tight' }}>
        {title}
      </Paragraph>

      <Paragraph>{content}</Paragraph>
    </Box>
  );
});

RenterProfileAttribute.displayName = 'RenterProfileAttribute';
export default memo(RenterProfileAttributes);
